import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertColor } from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { AuthError } from 'auth/utils';
import { uploadReconciliationFile } from './api/reconciliationApi';

export type UploadStatus = {
  title: string;
  description?: string;
  severity: AlertColor;
};

const HISTORY_QUERY_KEY = ['subscriptionStatusReconciliation', 'history'];

export const useReconciliationUpload = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null);

  const { mutate: uploadFileMutation, isPending } = useMutation({
    mutationFn: async (file: File) => {
      return uploadReconciliationFile(file);
    },
    onSuccess: (response, file) => {
      setUploadStatus({
        title: t('admin.subscriptionStatusReconciliation.fileProcessedSuccessfully', {
          filename: file.name,
        }),
        description: t('admin.subscriptionStatusReconciliation.processedWithSkipped', {
          processed: response.processedCount,
          skipped: response.skippedCount,
        }),
        severity: 'success',
      });
      queryClient.invalidateQueries({ queryKey: HISTORY_QUERY_KEY });
      setFile(null);
    },
    onError: (error, file) => {
      if (error instanceof AuthError) {
        throw error;
      }

      setUploadStatus({
        title: t('admin.subscriptionStatusReconciliation.fileUploadFailed', {
          filename: file.name,
        }),
        severity: 'error',
      });
    },
  });

  const uploadFile = () => {
    if (file) {
      uploadFileMutation(file);
    }
  };

  const clear = () => {
    setFile(null);
    setUploadStatus(null);
  };

  return {
    selectedFile: file,
    selectFile: setFile,
    uploadFile,
    uploadStatus,
    setUploadStatus,
    uploading: isPending,
    clear,
  };
};
